import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import Table from "../../core/pagination/datatable";
import { usePrintContext } from "../../core/utils/printContext";
import { Button, Space, Tag as AntTag, Tabs } from "antd";
import {
  fetchCompanies,
  addCompany,
  editCompany,
  activateCompany,
  deactivateCompany,
} from "../../core/redux/company.slice";
import { fetchCompanyTypes } from "../../core/redux/companyType.slice";
import CompanyFormModal from "../../core/modals/peoples/CompaniesFormModal";
import { canSee } from "../../core/utils/permissions";
import CommonFooter from "../../core/common/footer/commonFooter";
import LoadingState from "../../core/common/LoadingState";
import StickyBar from "../../core/common/StickyBar";
import { useTranslation } from "react-i18next";

const getCompanyId = (c) => c?.companyId ?? c?.id;
const fmtDate = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "-");

const Companies = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { shopId } = useParams();
  const { companies, loading } = useSelector((state) => state.company);
  // const { companyTypes } = useSelector((state) => state.companyType);
  const { user } = useSelector((state) => state.auth);
  const canDelete = canSee(user?.roles, "SUPERADMIN");

  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isReadMode, setIsReadMode] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [filters, setFilters] = useState({
    nameCodeTax: "",
    phone: "",
    email: "",
  });
  const { getPdfColor, company: printCompany } = usePrintContext();

  useEffect(() => {
    dispatch(fetchCompanies());
    dispatch(fetchCompanyTypes());
  }, [dispatch]);

  // const typeName = (record) =>
  //   (companyTypes || []).find((ct) => ct.companyTypeId === record.companyTypeId)
  //     ?.companyTypeName || "-";

  const handleAdd = () => {
    setIsEditMode(false);
    setIsReadMode(false);
    setSelectedCompany(null);
    setShowModal(true);
  };

  const handleEdit = (company) => {
    setIsEditMode(true);
    setIsReadMode(false);
    setSelectedCompany(company);
    setShowModal(true);
  };

  const handleCancel = () => {
    setIsReadMode(false);
    setShowModal(false);
  };

  const handleActivate = (company) => {
    dispatch(activateCompany(getCompanyId(company)))
      .unwrap()
      .then(() => {
        dispatch(fetchCompanies());
      })
      .catch((error) => console.error("Error activating company:", error));
  };

  const handleDeactivate = (company) => {
    dispatch(deactivateCompany(getCompanyId(company)))
      .unwrap()
      .then(() => {
        dispatch(fetchCompanies());
      })
      .catch((error) => console.error("Error deactivating company:", error));
  };

  // const handleValidate = (company) => {
  //   dispatch(validateCompany(getCompanyId(company)))
  //     .unwrap()
  //     .then(() => {
  //       dispatch(fetchCompanies());
  //     })
  //     .catch((error) => console.error("Error validating company:", error));
  // };

  const handleFormSubmit = (values) => {
    const action = isEditMode
      ? editCompany({ id: getCompanyId(selectedCompany), company: values })
      : addCompany(values);

    dispatch(action)
      .unwrap()
      .then(() => {
        handleCancel();
        dispatch(fetchCompanies());
      })
      .catch((error) => console.error("Error saving company:", error));
  };

  const includes = (value, search) =>
    !search ||
    (value && String(value).toLowerCase().includes(search.toLowerCase()));

  const filteredCompanies = (Array.isArray(companies) ? companies : []).filter(
    (c) => {
      const nameCodeTaxMatch =
        !filters.nameCodeTax ||
        includes(c.companyCode, filters.nameCodeTax) ||
        includes(c.companyName, filters.nameCodeTax) ||
        includes(c.companyTaxNumber, filters.nameCodeTax);

      return (
        nameCodeTaxMatch &&
        includes(c.companyPhone, filters.phone) &&
        includes(c.companyEmail, filters.email)
      );
    },
  );

  // GROUPING LOGIC
  // Active : La société est active ET validée
  const activeCompanies = filteredCompanies.filter(
    (c) => c.companyIsActive && c.companyIsValidated,
  );

  // Trial (Essai) : La société est active MAIS pas encore validée
  const trialCompanies = filteredCompanies.filter(
    (c) => c.companyIsActive && !c.companyIsValidated,
  );

  // Inactive : La société n'est pas active
  const inactiveCompanies = filteredCompanies.filter((c) => !c.companyIsActive);

  const handlePrintCompanies = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const marginLeft = 12;
    const marginRight = 12;
    const marginTop = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - marginLeft - marginRight;

    doc.setFont("Helvetica", "bold");
    doc.setFontSize(18);
    doc.text(t("companies.list.title"), marginLeft, marginTop);

    doc.setFont("Helvetica", "normal");
    doc.setFontSize(9);

    const filterText = [
      filters.nameCodeTax
        ? `${t("companies.list.search")}: ${filters.nameCodeTax}`
        : null,
      filters.phone
        ? `${t("companies.columns.phone")}: ${filters.phone}`
        : null,
      filters.email
        ? `${t("companies.columns.email")}: ${filters.email}`
        : null,
    ]
      .filter(Boolean)
      .join(" | ");

    let y = marginTop + 8;
    doc.text(
      `${t("companies.list.printedDate")} ${dayjs().format("DD/MM/YYYY HH:mm")}`,
      marginLeft,
      y,
    );
    y += 5;
    doc.text(
      `${t("companies.list.nbrCompanies")}: ${filteredCompanies.length}`,
      marginLeft,
      y,
    );
    y += 5;

    if (filterText) {
      doc
        .splitTextToSize(
          `${t("companies.list.filters")}: ${filterText}`,
          contentWidth,
        )
        .forEach((line) => {
          doc.text(line, marginLeft, y);
          y += 4;
        });
    }
    y += 3;

    // CORPS DU TABLEAU PDF MIS À JOUR
    const body = filteredCompanies.map((c) => [
      [
        [c.companyCode, c.companyName].filter(Boolean).join(" - "),
        c.companyTaxNumber || "",
      ]
        .filter(Boolean)
        .join("\n") || "-",
      c.companyPhone || "-",
      c.companyEmail || "-",
      c.usersNumber ?? "-", // Nombre de shops
      c.licenseName || "-", // Nom de la licence
      c.companyIsActive ? t("common.active") : t("common.inactive"), // Statut simplifié pour le PDF
    ]);

    autoTable(doc, {
      startY: y,
      head: [
        [
          t("companies.columns.company"),
          t("companies.columns.phone"),
          t("companies.columns.email"),
          t("companies.columns.shops") || "Shops",
          t("companies.columns.license") || "License",
          t("companies.columns.status"),
        ],
      ],
      body,
      margin: { left: marginLeft, right: marginRight },
      styles: {
        font: "Helvetica",
        fontSize: 8,
        cellPadding: 2,
        valign: "middle",
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: getPdfColor("primary"),
        textColor: 255,
        halign: "center",
      },
      // LARGEURS DES COLONNES AJUSTÉES (6 colonnes au lieu de 8)
      columnStyles: {
        0: { cellWidth: contentWidth * 0.3 }, // Company
        1: { cellWidth: contentWidth * 0.15 }, // Phone
        2: { cellWidth: contentWidth * 0.2 }, // Email
        3: { cellWidth: contentWidth * 0.1, halign: "center" }, // Shops
        4: { cellWidth: contentWidth * 0.15 }, // License
        5: { cellWidth: contentWidth * 0.1, halign: "center" }, // Status
      },
      theme: "striped",
      didDrawPage: () => {
        const footerY = doc.internal.pageSize.getHeight() - 8;
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `${printCompany?.companyName || "App"} - ${t("companies.list.title")}`,
          pageWidth / 2,
          footerY,
          { align: "center" },
        );
      },
    });

    doc.autoPrint();
    window.open(doc.output("bloburl"), "_blank");
  };

  const columns = [
    {
      title: t("companies.columns.company"),
      dataIndex: "companyName",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: "bold" }}>
            <Link to={`/${shopId}/companies/${getCompanyId(record)}`}>
              {[record.companyCode, record.companyName]
                .filter(Boolean)
                .join(" - ")}
            </Link>
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {record.companyTaxNumber}
          </div>
        </div>
      ),
    },
    {
      title: t("companies.columns.phone"),
      dataIndex: "companyPhone",
      render: (v) => v || "-",
    },
    {
      title: t("companies.columns.email"),
      dataIndex: "companyEmail",
      render: (v) => v || "-",
    },

    {
      title: t("companies.columns.location") || "Location",
      key: "location",
      width: 160,
      render: (_, record) => {
        // Handle both camelCase and snake_case from .NET backend
        const country = record.companyCountry || record.company_country;
        const town = record.companyTown || record.company_town;

        // If neither exists, show a dash
        if (!country && !town) return <span style={{ color: "#ccc" }}>-</span>;

        return (
          <div
            style={{ display: "flex", alignItems: "center", fontSize: "13px" }}
          >
            <i
              className="ti ti-map-pin me-1"
              style={{ color: "#fa8c16", fontSize: "15px" }}
            ></i>
            <span>
              {country && town ? `${country} - ${town}` : country || town}
            </span>
          </div>
        );
      },
    },
    // License End Date Column
    {
      title: t("companies.columns.licenseEnd"),
      dataIndex: "licenseEndDate",
      render: (_, record) => {
        const endDate = record.licenseEndDate ?? record.license_end_date;
        if (!endDate) return "-";

        const expired = dayjs(endDate).isBefore(dayjs(), "day");
        return (
          <AntTag color={expired ? "error" : "success"}>
            {fmtDate(endDate)}
          </AntTag>
        );
      },
      width: 120,
    },

    // Trial End Date Column (NEW)
    {
      title: t("companies.columns.trialEnd") || "Trial End",
      dataIndex: "trialPeriodEndDate",
      render: (_, record) => {
        const isTrial =
          record.companyIsInTrialPeriod ?? record.company_is_in_trial_period;
        const endDate =
          record.trialPeriodEndDate ?? record.trial_period_end_date;

        if (!endDate) return "-";

        const expired = dayjs(endDate).isBefore(dayjs(), "day");
        return (
          <AntTag color={isTrial ? (expired ? "error" : "warning") : "default"}>
            {fmtDate(endDate)}
          </AntTag>
        );
      },
      width: 120,
    },

    // NOUVELLES COLONNES
    {
      title: t("companies.columns.numbers") || "Shops & Users",
      key: "shopsAndUsers",
      width: 140,
      render: (_, record) => {
        // 1. Shops Count: Filter by Company ID AND Active status
        const shopsCount =
          record.shops?.filter((shop) => {
            // Get IDs (handling both PascalCase and camelCase from backend)
            const shopCompanyId = shop.CompanyId || shop.companyId;
            const currentCompanyId = record.companyId || record.company_id;

            // Check if active (handling both casings)
            const isActive = shop.ShopIsActive || shop.shopIsActive;

            // Return true only if it belongs to this company AND is active
            return shopCompanyId === currentCompanyId && isActive;
          }).length ?? 0;

        // 2. Users Count: From the companyEmployeesNumber field
        const usersCount =
          record.companyEmployeesNumber ?? record.company_employees_number ?? 0;

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              fontSize: "13px",
              lineHeight: "1.2",
            }}
          >
            {/* Shops Row */}
            <div style={{ display: "flex", alignItems: "center" }}>
              <i
                className="ti ti-building-store me-1"
                style={{ color: "#1890ff", fontSize: "14px" }}
              ></i>
              <span style={{ fontWeight: 600 }}>{shopsCount}</span>
              <span
                style={{ color: "#888", fontSize: "11px", marginLeft: "4px" }}
              >
                {t("companies.columns.shops") || "Shops"}
              </span>
            </div>

            {/* Users Row */}
            <div style={{ display: "flex", alignItems: "center" }}>
              <i
                className="ti ti-users me-1"
                style={{ color: "#52c41a", fontSize: "14px" }}
              ></i>
              <span style={{ fontWeight: 600 }}>{usersCount}</span>
              <span
                style={{ color: "#888", fontSize: "11px", marginLeft: "4px" }}
              >
                {t("companies.columns.users") || "Users"}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: t("companies.columns.license") || "License",
      dataIndex: "licenseName",
      render: (v) => (
        <AntTag color="blue" style={{ fontWeight: 500 }}>
          {v || "-"}
        </AntTag>
      ),
      width: 120,
    },
    {
      title: t("companies.columns.status"),
      dataIndex: "companyIsActive",
      render: (active, record) => (
        <Space size={4} wrap>
          <AntTag color={active ? "success" : "default"}>
            {active ? t("common.active") : t("common.inactive")}
          </AntTag>
          {active && !record.companyIsValidated && (
            <AntTag color="warning">{t("companies.groups.trial")}</AntTag>
          )}
          {record.companyIsValidated && (
            <AntTag color="processing">
              {t("companies.columns.validated")}
            </AntTag>
          )}
        </Space>
      ),
      width: 150,
    },
    {
      title: t("companies.columns.actions"),
      dataIndex: "actions",
      key: "actions",
      width: 140,
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            {/* --- INACTIVE COMPANIES: Show ONLY Activate --- */}
            {!record.companyIsActive ? (
              canDelete && (
                <Link
                  className="me-2 p-2"
                  to="#"
                  onClick={() => handleActivate(record)}
                  title={t("common.activate") || "Activate"}
                >
                  <i
                    data-feather="play-circle"
                    className="feather-play-circle text-success"
                  ></i>
                </Link>
              )
            ) : (
              /* --- ACTIVE COMPANIES: Show Edit, Setup, Validate, Deactivate --- */
              <>
                {/* 1. Edit Button */}
                <Link
                  className="me-2 p-2"
                  to="#"
                  onClick={() => handleEdit(record)}
                  title={t("common.edit") || "Edit"}
                >
                  <i data-feather="edit" className="feather-edit"></i>
                </Link>

                {/* 2. Setup / Import Button (Only for Active) */}
                <Link
                  className="me-2 p-2"
                  to={`/${shopId}/companies/${getCompanyId(record)}/setup`}
                  title={t("companies.actions.setup") || "Setup & Import"}
                >
                  <i
                    data-feather="upload"
                    className="feather-upload text-info"
                  ></i>
                </Link>

                {/* 3. Admin-only actions: Validate & Deactivate */}
                {canDelete && (
                  <>
                    {!record.companyIsValidated && (
                      <Link
                        className="me-2 p-2"
                        to={`/${shopId}/companies/${getCompanyId(record)}`}
                        title={
                          t("common.reviewAndValidate") || "Review & Validate"
                        }
                      >
                        <i
                          data-feather="check-circle"
                          className="feather-check-circle text-primary"
                        ></i>
                      </Link>
                    )}
                    <Link
                      className="me-2 p-2"
                      to="#"
                      onClick={() => handleDeactivate(record)}
                      title={t("common.deactivate") || "Deactivate"}
                    >
                      <i
                        data-feather="pause-circle"
                        className="feather-pause-circle text-warning"
                      ></i>
                    </Link>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      ),
    },
  ];

  const tabItems = [
    {
      key: "active",
      label: (
        <span>
          {t("companies.groups.active")}{" "}
          <AntTag color="success">{activeCompanies.length}</AntTag>
        </span>
      ),
      children: (
        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={activeCompanies}
            rowKey={(r) => getCompanyId(r)}
            locale={{ emptyText: t("common.noData") || "No data available" }}
          />
        </div>
      ),
    },
    {
      key: "trial",
      label: (
        <span>
          {t("companies.groups.trial")}{" "}
          <AntTag color="warning">{trialCompanies.length}</AntTag>
        </span>
      ),
      children: (
        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={trialCompanies}
            rowKey={(r) => getCompanyId(r)}
            locale={{ emptyText: t("common.noData") || "No data available" }}
          />
        </div>
      ),
    },
    {
      key: "inactive",
      label: (
        <span>
          {t("companies.groups.inactive")}{" "}
          <AntTag color="default">{inactiveCompanies.length}</AntTag>
        </span>
      ),
      children: (
        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={inactiveCompanies}
            rowKey={(r) => getCompanyId(r)}
            locale={{ emptyText: t("common.noData") || "No data available" }}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <StickyBar
            title={t("companies.list.title")}
            count={filteredCompanies.length}
            actions={
              <Space size="middle">
                <Button className="btn-customized-primary" onClick={handleAdd}>
                  <i className="ti ti-circle-plus me-1"></i>
                  {t("companies.list.addButton")}
                </Button>
                <Button
                  className="btn-customized-primary"
                  onClick={handlePrintCompanies}
                  disabled={filteredCompanies.length === 0}
                >
                  <i className="ti ti-printer me-1"></i>
                  {t("common.print")}
                </Button>
              </Space>
            }
          />

          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set d-flex gap-3 flex-wrap">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("companies.list.searchCodeNameTax")}
                    value={filters.nameCodeTax}
                    onChange={(e) =>
                      setFilters({ ...filters, nameCodeTax: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("companies.list.searchPhone")}
                    value={filters.phone}
                    onChange={(e) =>
                      setFilters({ ...filters, phone: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("companies.list.searchEmail")}
                    value={filters.email}
                    onChange={(e) =>
                      setFilters({ ...filters, email: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <LoadingState
                  type="section"
                  message={t("common.loadingData")}
                />
              ) : (
                <div style={{ padding: "16px" }}>
                  <Tabs
                    defaultActiveKey="active"
                    items={tabItems}
                    style={{ marginTop: "8px" }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      <CompanyFormModal
        open={showModal}
        onCancel={handleCancel}
        onSubmit={handleFormSubmit}
        isEdit={isEditMode}
        isReadMode={isReadMode}
        initialData={selectedCompany}
      />
    </div>
  );
};

export default Companies;
